import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { resumeService, getBlobErrorMessage } from '../services/api';
import { convertParsedToResumeRequest, toResumeRequest } from '../utils/resumeCompare';

import { Resume, ParsedData } from '../types';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import UploadResume from '../components/UploadResume';
import ResumeAnalyticsModal from '../components/ResumeAnalyticsModal';
import ResumePreviewModal from '../components/ResumePreviewModal';
import ResumeCardPreview from '../components/ResumeCardPreview';
import { DashboardSkeleton } from '../components/Skeleton';
import { FileText, Upload, Edit3, Download, Trash2, Share2, BarChart3, Copy, Check, Eye, ChevronLeft, ChevronRight, Briefcase, CircleOff } from 'lucide-react';
import ApplicationFormModal from '../components/kanban/ApplicationFormModal';

type Step = 'menu' | 'upload';

const PAGE_SIZE = 6;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>('menu');
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [analyticsResumeId, setAnalyticsResumeId] = useState<number | null>(null);
  const [previewResume, setPreviewResume] = useState<Resume | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [applyResumeId, setApplyResumeId] = useState<number | null>(null);

  const totalPages = Math.max(1, Math.ceil(resumes.length / PAGE_SIZE));
  const paginatedResumes = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return resumes.slice(start, start + PAGE_SIZE);
  }, [resumes, page]);

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      const response = await resumeService.getAllResumes();
      setResumes(response.data);
    } catch (error) {
      console.error('Failed to fetch resumes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleParsed = async (data: ParsedData, _session: string) => {
    if (!user?.id) return;
    try {
      await resumeService.saveProfile(user.id, convertParsedToResumeRequest(data, 'modern'));
    } catch {
      toast.error('Could not prepare resume data.');
      return;
    }
    navigate('/workspace');
  };

  const handleOpenResume = async (resume: Resume) => {
    if (!user?.id) return;
    try {
      const req = toResumeRequest(resume);
      await resumeService.saveProfile(user.id, req);
    } catch {
      toast.error('Could not open resume.');
      return;
    }
    navigate('/workspace');
  };

  const handleDelete = async (id: number) => {
    try {
      await resumeService.deleteResume(id);
      toast.success('Resume deleted successfully!');
      const remaining = resumes.length - 1;
      const newTotalPages = Math.max(1, Math.ceil(remaining / PAGE_SIZE));
      if (page > newTotalPages) setPage(newTotalPages);
      fetchResumes();
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (status === 409) {
        toast.error(message || 'This resume is linked to job applications. Delete those applications first.');
      } else {
        toast.error(message || 'Failed to delete resume.');
      }
    }
  };

  const handleTogglePublic = async (resume: Resume) => {
    const target = !resume.isPublic;
    try {
      const response = await resumeService.updatePublicStatus(resume.id, target);
      setResumes((prev) => prev.map((r) => (r.id === resume.id ? response.data : r)));
      toast.success(target ? 'Resume published! Share your link.' : 'Resume is now private.');
    } catch {
      toast.error('Failed to update share status.');
    }
  };

  const handleToggleDisabled = async (resume: Resume) => {
    const target = !resume.disabled;
    try {
      const response = await resumeService.setDisabled(resume.id, target);
      setResumes((prev) => prev.map((r) => (r.id === resume.id ? response.data : r)));
      toast.success(target ? 'Resume disabled — hidden from the application picker.' : 'Resume enabled.');
    } catch {
      toast.error('Failed to update resume status.');
    }
  };

  const handleCopyLink = async (resume: Resume) => {
    if (!resume.publicSlug) return;
    const url = `${window.location.origin}/r/${resume.publicSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(resume.publicSlug);
      setTimeout(() => setCopiedSlug(null), 2000);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Could not copy link.');
    }
  };

  const handleGeneratePdf = async (templateId: string, cvData: Record<string, unknown>) => {
    try {
      const response = await resumeService.generateResume(cvData as unknown as Resume);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const name = (cvData as any).personalInfo?.fullName || 'Resume';
      link.href = url;
      link.download = `${name}_${templateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Resume downloaded successfully!');
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 403) {
        toast.error('This resume uses a Pro template. Upgrade to download it.');
        navigate('/pricing');
      } else if (status === 503) {
        toast.error('Payments are temporarily unavailable. You can download once payments are re-enabled and you have an active subscription.');
      } else {
        toast.error(await getBlobErrorMessage(error));
      }
    }
  };

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (currentStep === 'upload') {
    return (
      <UploadResume
        onParsed={handleParsed}
        onBack={() => setCurrentStep('menu')}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
          Resume Builder
        </h1>
      </div>

      <div className="space-y-10">
        {/* Primary Actions */}
        <motion.div
          className="grid sm:grid-cols-2 gap-5"
          initial="hidden"
          animate="visible"
        >
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.3 }}
            onClick={() => setCurrentStep('upload')}
            className="bg-white border border-zinc-200 rounded-card p-6 sm:p-8 text-left cursor-pointer transition-all duration-200 hover:shadow-elevated hover:-translate-y-0.5 group"
          >
            <div className="w-10 h-10 rounded-btn bg-brand-50 flex items-center justify-center mb-4 group-hover:bg-brand-100 transition-colors">
              <Upload className="w-5 h-5 text-brand-500" />
            </div>
            <h3 className="font-display font-semibold text-foreground text-lg mb-1.5">
              Create from Resume
            </h3>
            <p className="text-sm text-zinc-500 leading-relaxed">
              Upload your resume and we'll extract the information automatically
            </p>
          </motion.button>

          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            onClick={() => navigate('/manual-create')}
            className="bg-white border border-zinc-200 rounded-card p-6 sm:p-8 text-left cursor-pointer transition-all duration-200 hover:shadow-elevated hover:-translate-y-0.5 group"
          >
            <div className="w-10 h-10 rounded-btn bg-brand-50 flex items-center justify-center mb-4 group-hover:bg-brand-100 transition-colors">
              <Edit3 className="w-5 h-5 text-brand-500" />
            </div>
            <h3 className="font-display font-semibold text-foreground text-lg mb-1.5">
              Update Resume Info
            </h3>
            <p className="text-sm text-zinc-500 leading-relaxed">
              Edit your resume details step by step
            </p>
          </motion.button>
        </motion.div>

        {/* Saved Resumes */}
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight text-foreground mb-5">
            Saved Resumes
          </h2>
          <AnimatePresence mode="wait">
            {resumes.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-white border border-zinc-200 rounded-card p-10 sm:p-14 text-center"
              >
                <div className="w-14 h-14 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-zinc-400" />
                </div>
                <h3 className="font-display font-semibold text-lg text-foreground mb-2">
                  Create your first professional resume
                </h3>
                <p className="text-sm text-zinc-500 max-w-sm mx-auto mb-6">
                  Select an onboarding path to generate a tailored, modern resume using our premium ATS optimization models.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                  <button
                    onClick={() => setCurrentStep('upload')}
                    className="h-10 px-5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center justify-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Import from Existing PDF
                  </button>
                  <button
                    onClick={() => navigate('/manual-create')}
                    className="h-10 px-5 border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center justify-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Build from Blank Slate
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="grid"
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                initial="hidden"
                animate="visible"
              >
                {paginatedResumes.map((resume, i) => (
                  <motion.div
                    key={resume.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.05, duration: 0.25 }}
                    className="bg-white border border-zinc-200 rounded-card shadow-soft overflow-hidden hover:shadow-elevated transition-all duration-300"
                  >
                    <div className={resume.disabled ? 'opacity-50 pointer-events-none' : ''}>
                      <ResumeCardPreview
                        resume={resume}
                        onOpen={() => handleOpenResume(resume)}
                        onPreview={() => setPreviewResume(resume)}
                      />
                    </div>
                    <div className="px-3 py-2.5 border-t border-zinc-100">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-foreground truncate">
                          {resume.personalInfo.fullName}
                        </p>
                        {resume.disabled ? (
                          <span className="text-[10px] font-semibold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 shrink-0">
                            <CircleOff className="w-2.5 h-2.5" /> Disabled
                          </span>
                        ) : resume.isPublic ? (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 shrink-0">
                            <Share2 className="w-2.5 h-2.5" /> Public
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded shrink-0">
                            Draft
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-400 truncate mt-0.5">
                        {resume.personalInfo.email} · <span className="text-zinc-500">{resume.templateId}</span>
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2.5 border-t border-zinc-100">
                        {!resume.disabled && (
                          <button
                            className="h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-btn transition-colors"
                            onClick={() => setApplyResumeId(resume.id)}
                          >
                            <Briefcase className="w-3 h-3" />
                            Apply
                          </button>
                        )}
                        <button
                          className="h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-medium text-zinc-600 bg-zinc-50 hover:bg-zinc-100 rounded-btn transition-colors"
                          onClick={() => setPreviewResume(resume)}
                        >
                          <Eye className="w-3 h-3" />
                          Preview
                        </button>
                        <button
                          className="h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-medium text-zinc-600 bg-zinc-50 hover:bg-zinc-100 rounded-btn transition-colors"
                          onClick={() => handleGeneratePdf(resume.templateId, resume as unknown as Record<string, unknown>)}
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </button>
                        <button
                          className={`h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-medium rounded-btn transition-colors ${
                            resume.isPublic
                              ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                              : 'text-zinc-600 bg-zinc-50 hover:bg-zinc-100'
                          }`}
                          onClick={() => handleTogglePublic(resume)}
                        >
                          <Share2 className="w-3 h-3" />
                          {resume.isPublic ? 'Public' : 'Share'}
                        </button>
                        <button
                          className="h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-medium text-zinc-600 bg-zinc-50 hover:bg-zinc-100 rounded-btn transition-colors"
                          onClick={() => setAnalyticsResumeId(resume.id)}
                        >
                          <BarChart3 className="w-3 h-3" />
                          Stats
                        </button>
                        <button
                          className={`h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-medium rounded-btn transition-colors ${
                            resume.disabled
                              ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                              : 'text-zinc-600 bg-zinc-50 hover:bg-zinc-100'
                          }`}
                          onClick={() => handleToggleDisabled(resume)}
                        >
                          <CircleOff className="w-3 h-3" />
                          {resume.disabled ? 'Enable' : 'Disable'}
                        </button>
                        <button
                          className="h-7 px-2.5 inline-flex items-center gap-1 text-[11px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-btn transition-colors ml-auto"
                          onClick={() => handleDelete(resume.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                      {resume.isPublic && resume.publicSlug && (
                        <div className="mt-2 flex items-center gap-2 bg-zinc-50 rounded-btn px-2.5 py-1.5">
                          <span className="text-xs text-zinc-500 truncate flex-1">
                            {window.location.origin}/r/{resume.publicSlug}
                          </span>
                          <button
                            className="text-zinc-500 hover:text-brand-600 transition-colors"
                            onClick={() => handleCopyLink(resume)}
                            aria-label="Copy public link"
                          >
                            {copiedSlug === resume.publicSlug ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
          {resumes.length > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-2 mt-5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8 w-8 inline-flex items-center justify-center rounded-btn border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`h-8 w-8 inline-flex items-center justify-center rounded-btn text-sm font-medium transition-colors ${
                    p === page
                      ? 'bg-brand-500 text-white'
                      : 'border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="h-8 w-8 inline-flex items-center justify-center rounded-btn border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {analyticsResumeId !== null && (
        <ResumeAnalyticsModal resumeId={analyticsResumeId} onClose={() => setAnalyticsResumeId(null)} />
      )}

      {previewResume && (
        <ResumePreviewModal resume={previewResume} onClose={() => setPreviewResume(null)} />
      )}

      {applyResumeId !== null && (
        <ApplicationFormModal
          resumes={resumes}
          initialResumeId={applyResumeId}
          onClose={() => setApplyResumeId(null)}
          onSaved={() => {
            setApplyResumeId(null);
            toast.success('Application added — find it on your board.');
            navigate('/applications');
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
